import * as FileSystem from 'expo-file-system';

export type ColorPalette = {
  id: string;
  name: string;
  colors: string[];
};

export type LightingCondition = {
  id: string;
  name: string;
  description: string;
  adjustment: 'warmer' | 'cooler' | 'neutral';
};

export type ColorFeedback = {
  color: string;
  colorName: string;
  feedback: string;
  tips: string;
  match: 'excellent' | 'good' | 'neutral' | 'poor';
};

// Dummy color palettes
export const COLOR_PALETTES: ColorPalette[] = [
  {
    id: '1',
    name: 'Spring',
    colors: ['#C4DFDF', '#D2E0FB', '#FFEAD2', '#F1F7B5', '#A8DF8E', '#E3F2C1'],
  },
  {
    id: '2',
    name: 'Summer',
    colors: ['#8ECAE6', '#219EBC', '#023047', '#FFB703', '#FB8500', '#FFE5E5'],
  },
  {
    id: '3',
    name: 'Autumn',
    colors: ['#865439', '#C68B59', '#D7B19D', '#8E3200', '#A64B2A', '#D8AD7F'],
  },
  {
    id: '4',
    name: 'Winter',
    colors: ['#27374D', '#526D82', '#9DB2BF', '#DDE6ED', '#102C57', '#0766AD'],
  },
  {
    id: '5',
    name: 'Neutrals',
    colors: ['#F8F0E5', '#EADBC8', '#DAC0A3', '#0F2C59', '#F8F9FA', '#CED4DA'],
  },
];

// Lighting conditions
export const LIGHTING_CONDITIONS: LightingCondition[] = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Balanced indoor lighting, suitable for most environments',
    adjustment: 'neutral',
  },
  {
    id: 'tropical',
    name: 'Tropical',
    description: 'Bright, warm sunlight with high intensity',
    adjustment: 'warmer',
  },
  {
    id: 'nordic',
    name: 'Nordic',
    description: 'Cool, blue-tinted natural light common in northern regions',
    adjustment: 'cooler',
  },
  {
    id: 'office',
    name: 'Office',
    description: 'Fluorescent lighting typical in office environments',
    adjustment: 'cooler',
  },
  {
    id: 'evening',
    name: 'Evening',
    description: 'Warm, golden lighting during sunset or with indoor lamps',
    adjustment: 'warmer',
  },
];

// Color name mapping for feedback
export const COLOR_NAMES: Record<string, string> = {
  '#C4DFDF': 'light blue',
  '#D2E0FB': 'soft blue',
  '#FFEAD2': 'peachy beige',
  '#F1F7B5': 'light yellow',
  '#A8DF8E': 'mint green',
  '#E3F2C1': 'light lime',
  '#8ECAE6': 'sky blue',
  '#219EBC': 'teal blue',
  '#023047': 'navy blue',
  '#FFB703': 'amber',
  '#FB8500': 'orange',
  '#FFE5E5': 'pale pink',
  '#865439': 'chocolate brown',
  '#C68B59': 'caramel',
  '#D7B19D': 'tan',
  '#8E3200': 'rust',
  '#A64B2A': 'terracotta',
  '#D8AD7F': 'sand',
  '#27374D': 'slate',
  '#526D82': 'steel blue',
  '#9DB2BF': 'pewter',
  '#DDE6ED': 'pale gray',
  '#102C57': 'midnight blue',
  '#0766AD': 'cobalt blue',
  '#F8F0E5': 'cream',
  '#EADBC8': 'beige',
  '#DAC0A3': 'taupe',
  '#0F2C59': 'dark navy',
  '#F8F9FA': 'off-white',
  '#CED4DA': 'silver',
};

// Feedback options
const FEEDBACK_OPTIONS = [
  'works well',
  'creates a beautiful contrast',
  'harmonizes nicely',
  'might be overwhelming',
  'brings out the warmth',
  'enhances the coolness',
  'creates balance',
  'provides a striking accent',
];

// Tip options
const TIP_OPTIONS = [
  'Consider using this color for tops or accessories.',
  'This would work beautifully in your spring/summer wardrobe.',
  'Try pairing with neutral tones for a balanced look.',
  'This color can bring energy to your outfit.',
  'This shade may be better as an accent rather than a dominant color.',
  'This would be perfect for special occasions.',
  'Consider this for casual everyday wear.',
];

/**
 * Get all color palettes
 */
export const getColorPalettes = async (): Promise<ColorPalette[]> => {
  // In a real app, this would be a call to the API
  // return await api.get('/color-palettes');

  // Return dummy data
  return COLOR_PALETTES;
};

/**
 * Get all lighting conditions
 */
export const getLightingConditions = async (): Promise<LightingCondition[]> => {
  // In a real app, this would be a call to the API
  // return await api.get('/lighting-conditions');

  // Return dummy data
  return LIGHTING_CONDITIONS;
};

/**
 * Adjust a color palette for a specific lighting condition
 */
export const adjustPaletteForLighting = (
  palette: ColorPalette,
  lighting: LightingCondition
): ColorPalette => {
  // Create a new palette with adjusted colors
  const adjustedColors = palette.colors.map((color) => {
    return adjustColorForLighting(color, lighting.adjustment);
  });

  return {
    id: `${palette.id}-${lighting.id}`,
    name: `${palette.name} (${lighting.name})`,
    colors: adjustedColors,
  };
};

/**
 * Helper function to adjust a color for different lighting conditions
 */
const adjustColorForLighting = (
  hexColor: string,
  adjustment: 'warmer' | 'cooler' | 'neutral'
): string => {
  if (adjustment === 'neutral') return hexColor;

  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  let adjustedR = r;
  let adjustedG = g;
  let adjustedB = b;

  // Adjust colors based on lighting condition
  if (adjustment === 'warmer') {
    // Increase red and yellow components, decrease blue
    adjustedR = Math.min(255, r + 15);
    adjustedG = Math.min(255, g + 5);
    adjustedB = Math.max(0, b - 10);
  } else if (adjustment === 'cooler') {
    // Increase blue component, decrease red
    adjustedR = Math.max(0, r - 10);
    adjustedG = Math.max(0, g - 5);
    adjustedB = Math.min(255, b + 15);
  }

  // Convert back to hex
  const toHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(adjustedR)}${toHex(adjustedG)}${toHex(adjustedB)}`;
};

/**
 * Get feedback for a specific color against a user's skin tone
 */
export const getColorFeedback = async (
  colorHex: string,
  imageUri?: string,
  skinTone?: string,
  lightingCondition?: string
): Promise<ColorFeedback> => {
  // In a real app, this would analyze the image and color with AI
  // return await api.post('/colors/analyze', { colorHex, imageUri, skinTone, lightingCondition });

  // If image is provided, validate it
  if (imageUri) {
    const fileInfo = await FileSystem.getInfoAsync(imageUri);
    if (!fileInfo.exists) {
      throw new Error('Image file does not exist');
    }
  }

  // Generate random feedback for demo purposes
  const feedbackIndex = Math.floor(Math.random() * FEEDBACK_OPTIONS.length);
  const tipIndex = Math.floor(Math.random() * TIP_OPTIONS.length);

  const matchOptions: ColorFeedback['match'][] = [
    'excellent',
    'good',
    'neutral',
    'poor',
  ];
  const randomMatch =
    matchOptions[Math.floor(Math.random() * matchOptions.length)];

  // Adjust feedback based on lighting condition if provided
  let adjustedFeedback = FEEDBACK_OPTIONS[feedbackIndex];
  if (lightingCondition) {
    const lighting = LIGHTING_CONDITIONS.find(
      (l) => l.id === lightingCondition
    );
    if (lighting) {
      if (lighting.adjustment === 'warmer') {
        adjustedFeedback += ' in warm lighting';
      } else if (lighting.adjustment === 'cooler') {
        adjustedFeedback += ' in cool lighting';
      }
    }
  }

  return {
    color: colorHex,
    colorName: COLOR_NAMES[colorHex] || 'custom color',
    feedback: adjustedFeedback,
    tips: TIP_OPTIONS[tipIndex],
    match: randomMatch,
  };
};
